import type { Key } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TeamRole } from '@/consts/team'

import { normalizeMember } from '../adapters/teamMember'
import type { MemberRow } from '../components/MemberTable'
import { fetchAllMembers } from '../services/member'
import { getTeamInfo } from '../services/team'

export type Filters = {
  status: 'all' | 'active' | 'inactive' | 'pending'
  bind: 'all' | 'bound' | 'unbound'
  search: string
}

type SortKey = keyof Pick<
  MemberRow,
  'id' | 'username' | 'status' | 'role' | 'bindStatus' | 'balance' | 'lastLogin'
>
type SortOrder = 'ascend' | 'descend' | null

const STATUS_RANK: Record<MemberRow['status'], number> = {
  active: 0,
  inactive: 1,
  uninitialized: 2,
  suspended: 3,
  deleted: 4,
}

const cmpStr = (a = '', b = '') => a.localeCompare(b, undefined, { sensitivity: 'base' })
const cmpNum = (a = 0, b = 0) => a - b
const cmpISO = (a?: string, b?: string) => (a ? Date.parse(a) : 0) - (b ? Date.parse(b) : 0)

const ROLE_RANK: Record<MemberRow['role'], number> = {
  [TeamRole.Invalid]: 0,
  [TeamRole.Owner]: 1,
  [TeamRole.Admin]: 2,
  [TeamRole.Normal]: 3,
}

export type StatsCardsModel = {
  activeMembers: number
  inactiveMembers: number
  administratorNumbers: number
  totalTeamSeats: number
}

const DEFAULT_STATS: StatsCardsModel = {
  activeMembers: 0,
  inactiveMembers: 0,
  administratorNumbers: 0,
  totalTeamSeats: 0,
}

const POLL_INTERVAL = 30 * 1000 // 30 seconds

export function useTeamMembers(teamResID?: string, initialPageSize = 5) {
  const [allRows, setAllRows] = useState<MemberRow[]>([])
  const [loading, setLoading] = useState(false)

  const [filters, setFilters] = useState<Filters>({ status: 'all', bind: 'all', search: '' })
  const [page, setPage] = useState(1)
  const [pageSize] = useState(initialPageSize)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [currentPageSelectedRowKeys, setCurrentPageSelectedRowKeys] = useState<string[]>([])

  const [stats, setStats] = useState<StatsCardsModel>(DEFAULT_STATS)
  const [teamDomain, setTeamDomain] = useState<string>('')

  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortOrder, setSortOrder] = useState<SortOrder>(null)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Silent refresh: fetch without showing loading state, preserves current page
  const refreshList = useCallback(async () => {
    if (!teamResID) return
    try {
      const [info, members] = await Promise.all([
        getTeamInfo(teamResID),
        fetchAllMembers(teamResID),
      ])
      if (!mountedRef.current) return
      setTeamDomain(info.domain || '')
      const normalized = (members ?? []).map((m: any) => normalizeMember(m))
      setAllRows(normalized)
      setStats((s) => ({
        ...s,
        administratorNumbers: info.adminsNumLimit ?? 0,
        totalTeamSeats: info.membersNumLimit ?? 0,
      }))
    } catch {
      // silent fail on background refresh
    }
  }, [teamResID])

  // initial fetch + polling
  useEffect(() => {
    if (!teamResID) return
    let alive = true
    let timer: ReturnType<typeof setInterval> | null = null

    async function run() {
      setLoading(true)
      try {
        if (!teamResID) return console.error('teamResID is required')
        const [info, members] = await Promise.all([
          getTeamInfo(teamResID),
          fetchAllMembers(teamResID),
        ])

        if (!alive) return

        setTeamDomain(info.domain || '')

        const normalized = (members ?? []).map((m: any) => normalizeMember(m))
        setAllRows(normalized)

        const active = normalized.reduce(
          (n: number, r: MemberRow) => n + (r.status === 'active' ? 1 : 0),
          0,
        )
        const inactive = normalized.reduce(
          (n: number, r: MemberRow) => n + (r.status === 'inactive' ? 1 : 0),
          0,
        )
        setStats({
          activeMembers: active,
          inactiveMembers: inactive,
          administratorNumbers: info.adminsNumLimit ?? 0,
          totalTeamSeats: info.membersNumLimit ?? 0,
        })

        setPage(1)
      } finally {
        if (alive) setLoading(false)
      }
    }

    run()
    timer = setInterval(refreshList, POLL_INTERVAL)

    return () => {
      alive = false
      if (timer) clearInterval(timer)
    }
  }, [teamResID, refreshList])

  // derived: filter + client-side pagination
  const filteredRows = useMemo(() => {
    // only show active and inactive users
    const visible = allRows.filter((r) => r.status === 'active' || r.status === 'inactive')

    return visible.filter((r) => {
      const okStatus = filters.status === 'all' || r.status === filters.status
      const okBind =
        filters.bind === 'all' || r.bindStatus === (filters.bind === 'bound' ? true : false)
      const q = filters.search.toLowerCase()
      const okSearch =
        !q || r.username.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
      return okStatus && okBind && okSearch
    })
  }, [allRows, filters])

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortOrder) return filteredRows
    const dir = sortOrder === 'ascend' ? 1 : -1
    const arr = [...filteredRows]
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'id':
          return dir * cmpStr(a.id, b.id)
        case 'username':
          return dir * cmpStr(a.username, b.username)
        case 'status':
          return dir * (STATUS_RANK[a.status] - STATUS_RANK[b.status])
        case 'role':
          return dir * (ROLE_RANK[a.role] - ROLE_RANK[b.role]) || cmpStr(a.username, b.username)
        case 'bindStatus':
          return dir * (a.bindStatus ? 1 : -1)
        case 'balance':
          return dir * cmpNum(a.balance, b.balance)
        case 'lastLogin':
          return dir * cmpISO(a.lastLogin, b.lastLogin)
        default:
          return 0
      }
    })
    return arr
  }, [filteredRows, sortKey, sortOrder])

  const total = sortedRows.length

  const numPages = Math.max(1, Math.ceil(total / pageSize))

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [sortedRows, page, pageSize])

  useEffect(() => {
    setPage(1)
  }, [filters.status, filters.bind, filters.search, sortKey, sortOrder])

  // clamp page when total shrinks
  useEffect(() => {
    if (page > numPages) setPage(numPages)
  }, [numPages, page])

  // derive current page selection keys from global selectedIds
  useEffect(() => {
    setCurrentPageSelectedRowKeys(pageRows.filter((r) => selectedIds.has(r.id)).map((r) => r.id))
  }, [pageRows, selectedIds])

  const onSortChange = (key: SortKey | null, order: SortOrder) => {
    setSortKey(key)
    setSortOrder(order)
  }

  // selection handler (keeps cross-page memory)
  const onSelectionChange = (keys: Key[]) => {
    const pageIds = pageRows.map((r) => r.id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of pageIds) next.delete(id) // clear this page old
      for (const k of keys) next.add(String(k)) // add this page new
      return next
    })
  }

  // when filters change, back to first page
  useEffect(() => {
    setPage(1)
  }, [filters.status, filters.bind])

  // recalc active/inactive whenever dataset changes
  useEffect(() => {
    const active = allRows.reduce((n, r) => n + (r.status === 'active' ? 1 : 0), 0)
    const inactive = allRows.reduce((n, r) => n + (r.status === 'inactive' ? 1 : 0), 0)
    setStats((s) => ({ ...s, activeMembers: active, inactiveMembers: inactive }))
  }, [allRows])

  return {
    // table data
    pageRows,
    total,
    page,
    setPage,
    pageSize,

    // filters
    filters,
    setFilters,

    // selection
    selectedIds,
    currentPageSelectedRowKeys,
    onSelectionChange,

    // stats & team meta
    stats,
    teamDomain,

    // misc
    loading,
    allRows,
    setAllRows,

    // sorting
    sortKey,
    sortOrder,
    onSortChange,

    // refresh
    refresh: refreshList,
  }
}
